import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  listOrganizations,
  createOrganization,
  getOrganization,
  updateOrganization,
  deleteOrganization,
  listMembers,
  updateMemberRole,
  removeMember,
  transferOwnership,
  createInvitation,
  listInvitations,
  revokeInvitation,
  acceptInvitation,
} from "../../services/Organization";

// ==================== Thunks ====================

export const fetchOrganizations = createAsyncThunk(
  "org/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await listOrganizations();
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const createOrg = createAsyncThunk(
  "org/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createOrganization(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchOrgDetails = createAsyncThunk(
  "org/fetchDetails",
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await getOrganization(orgId);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const updateOrg = createAsyncThunk(
  "org/update",
  async ({ orgId, data }, { rejectWithValue }) => {
    try {
      const response = await updateOrganization(orgId, data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const deleteOrg = createAsyncThunk(
  "org/delete",
  async (orgId, { rejectWithValue }) => {
    try {
      await deleteOrganization(orgId);
      return orgId;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchMembers = createAsyncThunk(
  "org/fetchMembers",
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await listMembers(orgId);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const changeMemberRole = createAsyncThunk(
  "org/changeMemberRole",
  async ({ orgId, memberId, role }, { rejectWithValue }) => {
    try {
      const response = await updateMemberRole(orgId, memberId, role);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const removeMemberFromOrg = createAsyncThunk(
  "org/removeMember",
  async ({ orgId, memberId }, { rejectWithValue }) => {
    try {
      await removeMember(orgId, memberId);
      return memberId;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const transferOrgOwnership = createAsyncThunk(
  "org/transferOwnership",
  async ({ orgId, memberId }, { rejectWithValue }) => {
    try {
      await transferOwnership(orgId, memberId);
      return { orgId, memberId };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const inviteMember = createAsyncThunk(
  "org/inviteMember",
  async ({ orgId, email, role }, { rejectWithValue }) => {
    try {
      const response = await createInvitation(orgId, { email, role });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchInvitations = createAsyncThunk(
  "org/fetchInvitations",
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await listInvitations(orgId);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const revokeOrgInvitation = createAsyncThunk(
  "org/revokeInvitation",
  async ({ orgId, invitationId }, { rejectWithValue }) => {
    try {
      await revokeInvitation(orgId, invitationId);
      return invitationId;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const acceptOrgInvitation = createAsyncThunk(
  "org/acceptInvitation",
  async (token, { rejectWithValue }) => {
    try {
      const response = await acceptInvitation(token);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

// ==================== Slice ====================

const orgSlice = createSlice({
  name: "org",
  initialState: {
    organizations: [],
    currentOrg: null,
    orgDetails: null,
    members: [],
    invitations: [],
    loading: false,
    error: null,
  },
  reducers: {
    setCurrentOrg: (state, action) => {
      state.currentOrg = action.payload;
      // Persist the selection
      if (action.payload?.id) {
        localStorage.setItem("currentOrgId", action.payload.id);
      }
    },
    clearOrgError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all orgs
      .addCase(fetchOrganizations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.loading = false;
        state.organizations = action.payload;
        // Auto-select current org if not set
        if (!state.currentOrg && action.payload.length > 0) {
          const savedOrgId = localStorage.getItem("currentOrgId");
          const saved = action.payload.find((o) => o.id === savedOrgId);
          state.currentOrg = saved || action.payload[0];
        }
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create org
      .addCase(createOrg.fulfilled, (state, action) => {
        state.organizations.push(action.payload);
      })

      // Fetch org details
      .addCase(fetchOrgDetails.fulfilled, (state, action) => {
        state.orgDetails = action.payload;
      })

      // Update org
      .addCase(updateOrg.fulfilled, (state, action) => {
        const idx = state.organizations.findIndex(
          (o) => o.id === action.payload.id,
        );
        if (idx !== -1)
          state.organizations[idx] = {
            ...state.organizations[idx],
            ...action.payload,
          };
        if (state.currentOrg?.id === action.payload.id) {
          state.currentOrg = { ...state.currentOrg, ...action.payload };
        }
      })

      // Delete org
      .addCase(deleteOrg.fulfilled, (state, action) => {
        state.organizations = state.organizations.filter(
          (o) => o.id !== action.payload,
        );
        if (state.currentOrg?.id === action.payload) {
          state.currentOrg = state.organizations[0] || null;
        }
      })

      // Members
      .addCase(fetchMembers.fulfilled, (state, action) => {
        state.members = action.payload;
      })
      .addCase(changeMemberRole.fulfilled, (state, action) => {
        const idx = state.members.findIndex((m) => m.id === action.payload.id);
        if (idx !== -1) state.members[idx] = action.payload;
      })
      .addCase(removeMemberFromOrg.fulfilled, (state, action) => {
        state.members = state.members.filter((m) => m.id !== action.payload);
      })

      // Invitations
      .addCase(fetchInvitations.fulfilled, (state, action) => {
        state.invitations = action.payload;
      })
      .addCase(inviteMember.fulfilled, (state, action) => {
        state.invitations.push(action.payload);
      })
      .addCase(revokeOrgInvitation.fulfilled, (state, action) => {
        state.invitations = state.invitations.filter(
          (i) => i.id !== action.payload,
        );
      })

      // Accept invitation
      .addCase(acceptOrgInvitation.fulfilled, (state, action) => {
        // The new org membership will be fetched via fetchOrganizations
      });
  },
});

export const { setCurrentOrg, clearOrgError } = orgSlice.actions;
export default orgSlice.reducer;
